/**
 * Collect completed research artifacts from workspace projects/studies tree.
 */

export function collectCompletedArtifacts(projects = [], artifactType, options = {}) {
  const { excludeStudyWithArtifactType } = options;
  const items = [];
  (projects || []).forEach((project) => {
    (project.studies || []).forEach((study) => {
      if (
        excludeStudyWithArtifactType &&
        (study.artifacts || []).some(
          (artifact) => artifact.artifact_type === excludeStudyWithArtifactType
        )
      ) {
        return;
      }
      (study.artifacts || []).forEach((artifact) => {
        if (artifact.artifact_type !== artifactType) return;
        if (artifact.status !== "completed") return;
        items.push({
          id: artifact.id,
          title: study.name || `${artifactType} #${artifact.id}`,
          createdAt: artifact.created_at || study.created_at || "",
          studyId: study.id,
          studySlug: study.slug || study.id,
          studyName: study.name || "",
          projectId: project.id,
          projectName: project.name || "",
          projectSlug: project.slug || project.id,
          content: artifact.content || "",
          artifactType: artifact.artifact_type,
        });
      });
    });
  });
  return items.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

export function formatArtifactDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const text = String(value);
    return text.slice(0, 10).replace(/-/g, ".");
  }
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}.${mm}.${dd}`;
}

function firstMeaningfulParagraph(text = "") {
  return String(text || "")
    .split(/\n+/)
    .filter((line) => !/^\s{0,3}#{1,6}\s+/.test(line))
    .map((line) => line.replace(/^[-*]\s+/, "").trim())
    .find((line) => line.length >= 8) || "";
}

function extractRelevantPlanText(content = "") {
  const paragraphs = String(content || "")
    .split(/\n\s*\n/)
    .map((paragraph) =>
      paragraph
        .split(/\r?\n/)
        .filter((line) => !/^\s{0,3}#{1,6}\s+/.test(line))
        .map((line) => line.replace(/^\s*[-*•]\s+/, "").trim())
        .filter(Boolean)
        .join(" ")
        .trim()
    )
    .filter((paragraph) => paragraph.length >= 8);

  if (!paragraphs.length) return "";

  const relevancePattern =
    /목적|목표|검증|가설|문제|사용자|사용성|과업|여정|흐름|경험|불편|개선|task|goal|usability|user/i;
  const relevantParagraphs = paragraphs.filter((paragraph) => relevancePattern.test(paragraph));

  // A user-edited plan may have no markdown headings or standard section
  // names. In that case, retain its first meaningful paragraphs rather than
  // falling back to the artifact title.
  return (relevantParagraphs.length ? relevantParagraphs : paragraphs).slice(0, 3).join("\n\n");
}

function extractSection(content = "", headings = []) {
  const lines = String(content || "").split(/\r?\n/);
  const normalizedHeadings = headings.map((h) => h.toLowerCase());
  let collecting = false;
  let sectionLevel = null;
  const collected = [];

  for (const line of lines) {
    const headingMatch = line.match(/^\s{0,3}(#{1,6})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const heading = headingMatch[2].trim().toLowerCase();
      const isTargetHeading = normalizedHeadings.some((target) => heading.includes(target));

      if (isTargetHeading && (!collecting || level <= sectionLevel)) {
        collecting = true;
        sectionLevel = level;
        continue;
      }
      // A lower-level heading belongs to the selected section. Only a heading
      // at the same or a higher level starts the next sibling/parent section.
      if (collecting && level <= sectionLevel) break;
    }
    if (collecting) collected.push(line);
  }
  return collected.join("\n").trim();
}

export function mapPlanContentToInterviewForm(plan, limits = {}) {
  const content = plan?.content || "";
  const studyName = plan?.title || plan?.studyName || "리서치 계획서 기반 인터뷰";
  const goalSection =
    extractSection(content, ["연구 목적", "조사 목적", "목적", "목표", "goal"]) ||
    firstMeaningfulParagraph(content);
  const productSection =
    extractSection(content, ["제품", "서비스", "배경", "대상", "product", "service"]) ||
    "";

  const clip = (value, max) => {
    const text = String(value || "").trim();
    return max ? text.slice(0, max) : text;
  };

  return {
    interviewName: clip(studyName, limits.interviewName),
    goal: clip(goalSection || `${studyName}의 핵심 가설과 검증 목적을 확인합니다.`, limits.goal),
    product: clip(productSection, limits.product),
  };
}

export function mapPlanContentToTestForm(plan) {
  const content = plan?.content || "";
  const name = plan?.title || plan?.studyName || "계획서 기반 테스트";
  const description =
    extractSection(content, ["연구 목적", "조사 목적", "목적", "목표", "검증"]) ||
    extractRelevantPlanText(content) ||
    firstMeaningfulParagraph(content) ||
    name;
  const serviceContext =
    extractSection(content, ["제품", "서비스", "배경", "대상"]) || "";

  return {
    name: String(name).trim().slice(0, 200),
    description: String(description).trim().slice(0, 2000),
    purpose: String(description).trim().slice(0, 2000),
    service_context: String(serviceContext).trim().slice(0, 2000),
    flow_goal: String(description).trim().slice(0, 2000),
  };
}

function collectQuestionLikeLines(text = "") {
  return String(text || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^[-*•]\s+/, "").replace(/^\d+[.)]\s+/, "").trim())
    .filter((line) => {
      if (!line || line.length < 6) return false;
      if (/^#{1,3}\s/.test(line)) return false;
      return /[?？]|까\s*$|나요\s*$|습니까|해주세요|말씀|경험|이유|어떻게|무엇|왜 /.test(line)
        || line.length >= 12;
    });
}

/**
 * Parse guideline markdown into an interview question draft snapshot.
 */
export function mapGuidelineContentToQuestionDraft(content = "") {
  const openingText = extractSection(content, ["오프닝", "도입", "워밍업", "opening"]);
  const closingText = extractSection(content, ["마무리", "클로징", "closing", "종료"]);
  const followUpText = extractSection(content, ["후속", "프로브", "probe", "follow"]);
  const mainText =
    extractSection(content, ["본 질문", "핵심 질문", "인터뷰 질문", "질문지", "질문", "메인"]) ||
    content;

  let opening = collectQuestionLikeLines(openingText).slice(0, 6);
  let closing = collectQuestionLikeLines(closingText).slice(0, 6);
  let followUps = collectQuestionLikeLines(followUpText).slice(0, 6);
  let mainQuestions = collectQuestionLikeLines(mainText);

  // Prefer questions that aren't already used in opening/closing
  const used = new Set([...opening, ...closing, ...followUps]);
  mainQuestions = mainQuestions.filter((q) => !used.has(q));

  if (mainQuestions.length === 0 && !opening.length) {
    mainQuestions = collectQuestionLikeLines(content).slice(0, 12);
  }

  if (!opening.length && mainQuestions.length > 2) {
    opening = mainQuestions.slice(0, 2);
    mainQuestions = mainQuestions.slice(2);
  }
  if (!closing.length && mainQuestions.length > 2) {
    closing = mainQuestions.slice(-2);
    mainQuestions = mainQuestions.slice(0, -2);
  }

  const chunkSize = Math.max(3, Math.ceil(Math.max(mainQuestions.length, 1) / 2));
  const taskGroups = [];
  for (let i = 0; i < mainQuestions.length; i += chunkSize) {
    const chunk = mainQuestions.slice(i, i + chunkSize);
    if (!chunk.length) continue;
    taskGroups.push({
      id: `task-${taskGroups.length + 1}`,
      title: `영역${taskGroups.length + 1}`,
      questions: chunk,
    });
  }

  if (!taskGroups.length && (opening.length || closing.length)) {
    taskGroups.push({
      id: "task-1",
      title: "핵심 질문",
      questions: [...opening, ...closing].slice(0, 4),
    });
  }

  if (!taskGroups.length) {
    return null;
  }

  return {
    opening: opening.length ? opening : [""],
    taskGroups,
    closing: closing.length ? closing : [""],
    followUps: followUps.length ? followUps : [""],
  };
}

export function emptyOneshotFormData(problemDefinition, studyName = "") {
  return {
    studyName: studyName || "",
    problemDefinition: String(problemDefinition || "").trim(),
    methodologies: [],
    timeline: "",
    startDate: "",
    endDate: "",
    budget: "",
    targetAudience: "",
    participantCount: "",
    additionalRequirements: "",
  };
}
